import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { ConfirmService } from './confirm.service';

/**
 * Renders the active {@link ConfirmService} prompt as a design-language modal.
 * Mount exactly once, inside the app shell. Closes on Escape or backdrop click;
 * focuses the confirm button when opened.
 */
@Component({
  selector: 'app-confirm-host',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ':host { display: contents; }',
  template: `
    @if (confirmService.current(); as prompt) {
      <div class="modal-backdrop" (click)="onBackdrop($event)">
        <div class="modal" role="dialog" aria-modal="true" [attr.aria-label]="prompt.title || 'Please confirm'">
          <div class="modal-header">
            <div class="modal-title">{{ prompt.title || 'Please confirm' }}</div>
          </div>
          <div class="modal-body">{{ prompt.message }}</div>
          <div class="modal-actions">
            <button class="btn btn-secondary btn-sm" type="button" (click)="cancel()">
              {{ prompt.cancelLabel || 'Cancel' }}
            </button>
            <button
              #confirmBtn
              class="btn btn-sm"
              [class.btn-danger]="prompt.danger"
              [class.btn-primary]="!prompt.danger"
              type="button"
              (click)="accept()"
            >
              {{ prompt.confirmLabel || 'Confirm' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
})
export class ConfirmHost {
  protected readonly confirmService = inject(ConfirmService);
  private readonly confirmBtn = viewChild<ElementRef<HTMLButtonElement>>('confirmBtn');

  constructor() {
    // Move focus to the confirm button once the dialog is rendered.
    effect(() => {
      const btn = this.confirmBtn();
      if (this.confirmService.current() && btn) {
        queueMicrotask(() => btn.nativeElement.focus());
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.confirmService.current()) {
      this.confirmService.dismiss();
    }
  }

  protected onBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.confirmService.dismiss();
    }
  }

  protected accept(): void {
    this.confirmService.accept();
  }

  protected cancel(): void {
    this.confirmService.dismiss();
  }
}
